// @ts-strict-ignore
import { useState, type JSX } from "react";

import { Dropdown, DropdownButton, DropdownContent, DropdownItem, Skeleton } from "@aviary";
import type { Brand, Variant } from "@shared/types/graphqlGenerated";

import * as styles from "./VariantSelect.styles";

type ItemDescriptor = JSX.Element | string;

type BrandName = Pick<Brand, "name">;

interface VariantSelectItems
  extends Pick<Variant, "id" | "descriptor" | "msrp" | "availabilityStatus" | "sku"> {
  brand: BrandName;
  dropdownItemDescriptor: ItemDescriptor;
  selectedItemDescriptor: ItemDescriptor;
}

interface Props {
  currentVariantId: string;
  onVariantSelect: (id: string) => void;
  variants: VariantSelectItems[];
  onOpen?: () => void;
  loading?: boolean;
}

const VariantSelect = ({ currentVariantId, onVariantSelect, variants, onOpen, loading }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentVariant = variants.find(variant => variant.id === currentVariantId) || variants[0];
  const sortedVariants = [...variants].sort((a, b) => a.msrp - b.msrp);

  const renderVariantDropdownItem = ({ id, dropdownItemDescriptor }) => (
    <DropdownItem key={id} data-testid={id} onSelect={() => onVariantSelect(id)}>
      {dropdownItemDescriptor}
    </DropdownItem>
  );

  const loadingSkeletons = () =>
    Array.from({ length: 3 }).map((_, index) => (
      <DropdownItem disabled key={index}>
        <Skeleton type="typography" typographySize="body" margin="topAndBottom" isFullWidth />
      </DropdownItem>
    ));

  const renderContent = () => {
    if (loading) {
      return loadingSkeletons();
    }

    return sortedVariants.map(variant => renderVariantDropdownItem(variant));
  };

  const handleDropdownToggle = () => {
    if (!isOpen) {
      onOpen?.();
    }
    setIsOpen(!isOpen);
  };

  return (
    <Dropdown isOpen={isOpen} triggerCallback={handleDropdownToggle}>
      <DropdownButton data-testid="select-button" css={styles.button} size="medium">
        {currentVariant.selectedItemDescriptor}
      </DropdownButton>
      <DropdownContent>{renderContent()}</DropdownContent>
    </Dropdown>
  );
};

export { VariantSelect };
export type { VariantSelectItems };
